<template>
  <div class="support-page">
    <!-- Hero Banner -->
    <section class="hero-banner">
      <div class="hero-content">
        <p class="hero-label">服务支持</p>
        <h1 class="hero-title">7×24 小时技术支持</h1>
        <p class="hero-subtitle">工单系统 · 在线客服 · 电话支持 · 远程协助</p>
      </div>
    </section>

    <!-- Main Content -->
    <div class="page-container">
      <!-- Support Channels -->
      <div class="channel-grid">
        <div class="channel-card" @click="handleChannel('online')">
          <div class="channel-icon-wrapper" style="background: #ebf5ff">
            <Icon icon="lucide:message-circle" :size="22" color="#0066FF" />
          </div>
          <h4 class="channel-name">在线客服</h4>
          <p class="channel-desc">平均响应 &lt;3秒</p>
        </div>
        <div class="channel-card" @click="handleChannel('phone')">
          <div class="channel-icon-wrapper" style="background: #ecfdf5">
            <Icon icon="lucide:phone" :size="22" color="#00B96B" />
          </div>
          <h4 class="channel-name">电话支持</h4>
          <p class="channel-desc">400-646-5288</p>
        </div>
        <div class="channel-card" @click="handleChannel('remote')">
          <div class="channel-icon-wrapper" style="background: #f3e8ff">
            <Icon icon="lucide:monitor" :size="22" color="#7C3AED" />
          </div>
          <h4 class="channel-name">远程协助</h4>
          <p class="channel-desc">专属客户经理</p>
        </div>
        <div class="channel-card" @click="handleChannel('email')">
          <div class="channel-icon-wrapper" style="background: #fffbeb">
            <Icon icon="lucide:mail" :size="22" color="#FF9500" />
          </div>
          <h4 class="channel-name">邮件支持</h4>
          <p class="channel-desc">kefu@zjsai.com</p>
        </div>
      </div>

      <!-- Ticket Form -->
      <h3 class="form-section-title">提交工单</h3>
      <div class="ticket-form">
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">联系人</label>
            <input v-model="contactName" class="form-input" placeholder="请输入联系人姓名" />
          </div>
          <div class="form-group">
            <label class="form-label">联系电话</label>
            <input
              v-model="contactPhone"
              class="form-input"
              type="tel"
              placeholder="请输入手机号"
            />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">工单类型</label>
            <select v-model="ticketType" class="form-select">
              <option value="technical">技术问题</option>
              <option value="account">账户问题</option>
              <option value="billing">计费问题</option>
              <option value="complaint">投诉建议</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">紧急程度</label>
            <select v-model="ticketPriority" class="form-select">
              <option value="normal">普通</option>
              <option value="urgent">紧急</option>
              <option value="critical">非常紧急</option>
            </select>
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">问题标题</label>
          <input
            v-model="ticketTitle"
            type="text"
            placeholder="请简要描述您的问题"
            class="form-input"
          />
        </div>
        <div class="form-group">
          <label class="form-label">问题描述</label>
          <textarea
            v-model="ticketDesc"
            placeholder="请详细描述您遇到的问题，包括操作步骤、截图等信息..."
            class="form-textarea"
          ></textarea>
        </div>
        <button class="submit-btn" @click="submitTicket">提交工单</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import { Icon } from '@/components/Icon'
import { getSubmitErrorMessage, submitPortalForm } from '../usePortalSubmission'

defineOptions({ name: 'SupportPage' })

const ticketType = ref<string>('technical')
const ticketPriority = ref<string>('normal')
const ticketTitle = ref<string>('')
const ticketDesc = ref<string>('')
const contactName = ref('')
const contactPhone = ref('')

function handleChannel(type: string) {
  const messages: Record<string, string> = {
    online: '正在接入在线客服...',
    phone: '已提交回拨请求',
    remote: '远程协助申请已提交',
    email: '邮件已发送'
  }
  ElMessage.success(messages[type] || '操作成功')
}

async function submitTicket() {
  if (!contactName.value.trim() || !contactPhone.value.trim()) {
    ElMessage.warning('请输入联系人和联系电话')
    return
  }
  if (!ticketTitle.value.trim()) {
    ElMessage.warning('请输入问题标题')
    return
  }
  if (!ticketDesc.value.trim()) {
    ElMessage.warning('请输入问题描述')
    return
  }
  try {
    const id = await submitPortalForm({
      username: contactName.value,
      tel: contactPhone.value,
      productName: ticketTitle.value,
      content: JSON.stringify({
        priority: ticketPriority.value,
        category: ticketType.value,
        description: ticketDesc.value
      }),
      type: 'SUPPORT_TICKET'
    })
    ElMessage.success(`工单已提交！工单号：CMS${id}`)
    contactName.value = ''
    contactPhone.value = ''
    ticketTitle.value = ''
    ticketDesc.value = ''
    ticketType.value = 'technical'
    ticketPriority.value = 'normal'
  } catch (error) {
    ElMessage.error(getSubmitErrorMessage(error))
  }
}
</script>

<style lang="scss" scoped>
.support-page {
  // Hero Banner
  .hero-banner {
    height: 200px;
    background: linear-gradient(135deg, #1f2937, #374151);
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #fff;
  }

  .hero-content {
    padding: 0 24px;
  }

  .hero-label {
    font-size: 14px;
    opacity: 0.6;
    letter-spacing: 4px;
    margin: 0 0 8px;
  }

  .hero-title {
    font-size: 36px;
    font-weight: 900;
    margin: 0 0 8px;
  }

  .hero-subtitle {
    font-size: 15px;
    opacity: 0.7;
    margin: 0;
  }

  // Page Container
  .page-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 40px 24px;
  }

  // Channel Grid
  .channel-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 40px;

    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .channel-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 24px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    }
  }

  .channel-icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;
  }

  .channel-name {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 4px;
    color: #111827;
  }

  .channel-desc {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
  }

  // Form Section
  .form-section-title {
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 20px;
    color: #111827;
  }

  .ticket-form {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 32px;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .form-group {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .form-label {
    font-size: 13px;
    color: #4b5563;
    margin-bottom: 6px;
    display: block;
  }

  .form-input,
  .form-select {
    width: 100%;
    height: 44px;
    padding: 0 14px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    background: #fff;
    transition: border-color 0.3s ease;
    box-sizing: border-box;

    &:focus {
      border-color: #0066ff;
    }
  }

  .form-select {
    cursor: pointer;
    appearance: auto;
  }

  .form-textarea {
    width: 100%;
    height: 120px;
    padding: 14px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    resize: none;
    font-family: inherit;
    transition: border-color 0.3s ease;
    box-sizing: border-box;

    &:focus {
      border-color: #0066ff;
    }

    &::placeholder {
      color: #9ca3af;
    }
  }

  .submit-btn {
    height: 44px;
    padding: 0 32px;
    background: #0066ff;
    color: #fff;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: #0044cc;
    }
  }
}
</style>
